import { Box, Stack } from "@mui/material";
import { memo, useCallback, useRef, useState, type KeyboardEvent } from "react";

import { toEnglishDigits } from "@/lib/format/digits";
import type { OtpInputProps } from "./OtpInput.types";
import * as styles from "./OtpInput.styles";

export type { OtpInputProps } from "./OtpInput.types";

export const OtpInput = memo(function OtpInput({
  value,
  onChange,
  length = 5,
  disabled = false,
  error = false,
}: OtpInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const digits = value.padEnd(length, " ").slice(0, length).split("");

  const updateValue = useCallback(
    (nextDigits: string[]) => {
      onChange(nextDigits.join("").trimEnd());
    },
    [onChange],
  );

  const handleChange = (index: number, rawValue: string) => {
    const normalized = toEnglishDigits(rawValue).replace(/\D/g, "").slice(-1);
    const nextDigits = [...digits.map((digit) => digit.trim())];
    nextDigits[index] = normalized;
    updateValue(nextDigits);

    if (normalized && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !digits[index]?.trim() && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = toEnglishDigits(event.clipboardData.getData("text"))
      .replace(/\D/g, "")
      .slice(0, length);

    if (!pasted) return;

    onChange(pasted);
    const focusIndex = Math.min(pasted.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <Stack sx={styles.root}>
      {digits.map((digit, index) => (
        <Box
          key={index}
          component="input"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit.trim()}
          disabled={disabled}
          sx={styles.digitInput(focusedIndex === index, error)}
          ref={(element: HTMLInputElement | null) => {
            inputRefs.current[index] = element;
          }}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex((current) => (current === index ? null : current))}
          onPaste={handlePaste}
        />
      ))}
    </Stack>
  );
});
